// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonBase,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { BiFile, BiCheckCircle } from 'react-icons/bi';

// TYPES ---------------------------------------------------------------- //

export interface ExportResultsDialogProps {
  open: boolean;
  onClose: () => void;
  /** The dialog's title, e.g. "Export Appointments". */
  title: string;
  /** The singular noun the copy counts, e.g. "appointment"; pluralized by adding an s. */
  noun: string;
  /** Every row "Export All Results" will write. `null` when the host cannot know it, so the copy carries no number. */
  totalCount: number | null;
  /** The rows on the visible page. */
  currentPageCount: number;
  /** Awaited for the loading state; a rejection keeps the dialog open so the user can retry or cancel. */
  onExportAll: () => Promise<void>;
  onExportCurrentPage: () => void;
}

// COMPONENT ---------------------------------------------------------------- //

/**
 * The portal's export choice, shared by every list that offers "all results" or "the current
 * page": the Search Results page and the Mobile Service Appointments tab. The host owns the
 * export itself and its toasts; this only picks which one runs and shows the fetch in flight.
 */
const ExportResultsDialog = ({
  open,
  onClose,
  title,
  noun,
  totalCount,
  currentPageCount,
  onExportAll,
  onExportCurrentPage,
}: ExportResultsDialogProps) => {
  const [loading, setLoading] = React.useState(false);
  const plural = `${noun}s`;
  const nounFor = (count: number) => (count === 1 ? noun : plural);

  const handleExportAll = async () => {
    setLoading(true);
    try {
      await onExportAll();
      onClose();
    } catch {
      // The host has already reported the failure; the dialog's only job here is to stay open.
    } finally {
      setLoading(false);
    }
  };

  const handleExportCurrentPage = () => {
    onExportCurrentPage();
    onClose();
  };

  // A real button, so the keyboard reaches each option (Tab) and starts it (Enter / Space).
  const optionSx = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    p: 2,
    opacity: loading ? 0.6 : 1,
    '&:hover': { backgroundColor: loading ? 'transparent' : 'action.hover' },
    '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
  } as const;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box display='flex' alignItems='center' gap={1}>
          <BiFile size={24} />
          {title}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant='body1' gutterBottom>
          Choose what you'd like to export:
        </Typography>

        <Box mt={2} display='flex' flexDirection='column' gap={2}>
          <ButtonBase sx={optionSx} disabled={loading} onClick={handleExportAll}>
            <Box display='flex' alignItems='center' gap={1} mb={1}>
              <BiCheckCircle size={20} />
              <Typography variant='subtitle1' fontWeight='bold'>
                Export All Results
              </Typography>
            </Box>
            <Typography variant='body2' color='text.secondary'>
              {totalCount === null
                ? `Export every matching ${noun}. This may take a moment for large datasets.`
                : `Export all ${totalCount.toLocaleString()} matching ${nounFor(totalCount)}. This may take a moment for large datasets.`}
            </Typography>
          </ButtonBase>

          <ButtonBase sx={optionSx} disabled={loading} onClick={handleExportCurrentPage}>
            <Box display='flex' alignItems='center' gap={1} mb={1}>
              <BiCheckCircle size={20} />
              <Typography variant='subtitle1' fontWeight='bold'>
                Export Current Page
              </Typography>
            </Box>
            <Typography variant='body2' color='text.secondary'>
              Export only the {currentPageCount} {nounFor(currentPageCount)} currently displayed on this page.
            </Typography>
          </ButtonBase>
        </Box>

        {loading && (
          <Box display='flex' alignItems='center' gap={1} mt={2}>
            <CircularProgress size={16} />
            <Typography variant='body2' color='text.secondary'>
              Fetching all {plural} for export...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default ExportResultsDialog;
